import { createSlice, isAnyOf } from "@reduxjs/toolkit";

import { USER_SETTINGS_KEY } from "core/local_storage_utils";
import { parseJsonString } from "core/str_utils";

import { editSomItem, fetchSomItem } from "./";
import { createSomOfferMonitoring } from "./services/createSomOfferMonitoring";
import { deleteSomOfferJust } from "./services/deleteSomOfferJust";
import { deleteSomOfferMonitoring } from "./services/deleteSomOfferMonitoring";
import { downloadSomOfferJust } from "./services/downloadSomOfferJust";
import { fetchSomOfferMonitoring } from "./services/fetchSomOfferMonitoring";
import { previewSomOfferJust } from "./services/previewSomOfferJust";
import { previewSomOfferMonitoringJust } from "./services/previewSomOfferMonitoringJust";
import { updateSomOffer } from "./services/updateSomOffer";
import { updateSomOfferMonitoring } from "./services/updateSomOfferMonitoring";
import { uploadSomOfferJusts } from "./services/uploadSomOfferJusts";

const defaultSomCurOfferMonitoring = { price_offer_without_vat: "", vat: 0.2 };

const initialState = () => {
	const userSettings = parseJsonString(localStorage.getItem(USER_SETTINGS_KEY));

	return {
		tableData: [],
		curItem: {},
		changeAction: {},
		changesCount: 0,
		fetchStatus: "",
		fetchStatusText: undefined,
		pdfBlobName: "",
		pdfBlobData: null,
		justPdfVisible: false,
		offerKeysWithExpandedJusts: [],
		offerKeysWithExpandedCompany: [],
		lastClickedRow: {},
		sourceInfoVisible: false,
		sourceInfoData: null,
		sourceInfoPriceOfferId: 0,
		innSearchModalVisible: false,
		innSearchModalObj: null,
		pushSelectedSourceRowOfferToTop: userSettings?.somItem?.pushSelectedSourceRowOfferToTop ?? false,
		somLeftExpanded: userSettings?.somItem?.somLeftExpanded ?? true,
		somLeftWidth: userSettings?.somItem?.somLeftWidth ?? 1350,
		somShowMonitoring: userSettings?.somItem?.somShowMonitoring ?? true,
		somOfferPriceOfferDb: {},
		somOfferMonitoring: [],
		somOfferMonitoringIndex: { MONITORING_1: 0, MONITORING_2: 1, MONITORING_3: 2 },
		somCurOfferMonitoring: defaultSomCurOfferMonitoring,
		deleteMonitoringConfirmDialog: { visible: false, objectId: null },
		deleteSourceDataConfirmDialog: { visible: false, objectId: null },
		deleteOfferConfirmDialog: { visible: false, objectId: null },
		savedMonitoringPrice: true,
	};
};

export const somSlice = createSlice({
	name: "som",
	initialState,
	reducers: {
		setTableData: (state, { payload }) => {
			state.tableData = payload;
		},
		setCurItem: (state, { payload }) => {
			state.curItem = payload;
		},
		editCurItemAttr: (state, { payload }) => {
			state.curItem = { ...state.curItem, [payload.key]: payload.value };
			state.changeAction = { key: "item", payload: { [payload.key]: payload.value }, action: "EDIT" };
			if (payload?.increaseChangesCount) state.changesCount += 1;
		},
		addCurItemChar: (state, { payload }) => {
			state.curItem.chars = [...state.curItem.chars.filter((x) => x.char_id !== -1), payload];
		},
		editCurItemChar: (state, { payload }) => {
			const { itemCharValueObj, increaseChangesCount } = payload;
			state.curItem.chars = state.curItem.chars.map((itemChar) => {
				if (itemChar.key === itemCharValueObj.key) itemChar = itemCharValueObj;
				return itemChar;
			});
			let key = "chars";
			if (payload?.actionKey) key = payload.actionKey;
			state.changeAction = { key: key, payload: itemCharValueObj };
			if (itemCharValueObj.id === 0) {
				state.changeAction.action = "ADD";
			} else {
				state.changeAction.action = "EDIT";
			}
			if (increaseChangesCount) state.changesCount += 1;
		},
		editCurItemCharType: (state, { payload }) => {
			const { itemCharValueObj } = payload;
			state.curItem.chars = state.curItem.chars.map((itemChar) => {
				if (itemChar.key === itemCharValueObj.key) itemChar = itemCharValueObj;
				return itemChar;
			});
			state.changeAction = {
				key: "char_prior_type",
				action: "EDIT",
				payload: itemCharValueObj,
			};
			state.changesCount += 1;
		},
		delCurItemChar: (state, { payload }) => {
			const { itemCharValue } = payload;
			state.curItem.chars = state.curItem.chars.filter((itemChar) => itemChar.id !== itemCharValue.id);
			state.changeAction = { key: "chars", payload: itemCharValue, action: "DEL" };
			state.changesCount += 1;
		},
		addPriceOfferCurItemOffer: (state, { payload }) => {
			const { priceOffersIds } = payload;
			state.changeAction = { key: "offers", payload: { ids: priceOffersIds }, action: "ADD" };
			state.changesCount += 1;
		},
		editCurItemOffer: (state, { payload }) => {
			const { offerObj, increaseChangesCount } = payload;
			state.curItem.offers = state.curItem.offers.map((offer) => {
				if (offer.id === offerObj.id) offer = offerObj;
				return offer;
			});
			state.changeAction = { key: "offers", payload: offerObj, action: "EDIT" };
			if (increaseChangesCount) state.changesCount += 1;
		},
		delCurItemOffer: (state, { payload }) => {
			state.changeAction = { key: "offers", payload: { id: payload.ids }, action: "DEL" };
			state.changesCount += 1;
		},
		addCurItemCls: (state, { payload }) => {
			const { clsObj } = payload;
			state.curItem.classifiers = [...state.curItem.classifiers, clsObj];
			state.changeAction = { key: "classifiers", payload: clsObj, action: "ADD" };
			state.changesCount += 1;
		},
		editCurItemCls: (state, { payload }) => {
			const { clsObj } = payload;
			state.curItem.classifiers = state.curItem.classifiers.map((cls) => {
				if (cls.id === clsObj.id) cls = clsObj;
				return cls;
			});
			state.changeAction = { key: "classifiers", payload: clsObj, action: "EDIT" };
			state.changesCount += 1;
		},
		delCurItemCls: (state, { payload }) => {
			const { clsObj } = payload;
			state.changeAction = { key: "classifiers", payload: clsObj, action: "DEL" };
			state.changesCount += 1;
		},
		increaseChangesCount: (state, { payload }) => {
			state.changesCount += 1;
		},
		setChangeAction: (state, { payload }) => {
			state.changeAction = payload;
		},
		editCurItemSourceData: (state, { payload }) => {
			const { sourceDataObj, increaseChangesCount } = payload;
			state.curItem.source_data = state.curItem.source_data.map((sourceData) => {
				if (sourceData.id === sourceDataObj.id) sourceData = sourceDataObj;
				return sourceData;
			});
			state.changeAction = { key: "source_data", payload: sourceDataObj, action: "EDIT" };
			if (increaseChangesCount) state.changesCount += 1;
		},
		addCurItemManuallySourceData: (state) => {
			state.changeAction = { key: "source_data_manually", payload: {}, action: "ADD" };
			if (increaseChangesCount) state.changesCount += 1;
		},
		addCurItemManuallySomOffer: (state, { payload }) => {
			const { parentId } = payload;
			state.changeAction = { key: "offers_manually", payload: { parent_id: parentId }, action: "ADD" };
			if (increaseChangesCount) state.changesCount += 1;
		},
		delCurItemSourceData: (state, { payload }) => {
			state.changeAction = { key: "source_data", payload: { id: payload.ids }, action: "DEL" };
			state.changesCount += 1;
		},
		setCurItemSourceData: (state, { payload }) => {
			state.curItem.source_data = payload;
		},
		setFetchStatus: (state, { payload }) => {
			state.fetchStatus = payload;
		},
		setFetchStatusText: (state, { payload }) => {
			state.fetchStatusText = payload;
		},
		setJustPdfVisible: (state, { payload }) => {
			state.justPdfVisible = payload;
		},
		setPdfBlobName: (state, { payload }) => {
			state.pdfBlobName = payload;
		},
		setPdfBlobData: (state, { payload }) => {
			state.pdfBlobData = payload;
		},
		setSavedMonitoringPrice: (state, { payload }) => {
			state.savedMonitoringPrice = payload;
		},
		setOfferKeysWithExpandedJusts: (state, { payload }) => {
			const keyIndex = state.offerKeysWithExpandedJusts.indexOf(payload);

			if (keyIndex === -1) {
				state.offerKeysWithExpandedJusts.push(payload);
			} else {
				state.offerKeysWithExpandedJusts.splice(keyIndex, 1);
			}
		},
		setOfferKeysWithExpandedCompany: (state, { payload }) => {
			const keyIndex = state.offerKeysWithExpandedCompany.indexOf(payload);

			if (keyIndex === -1) {
				state.offerKeysWithExpandedCompany.push(payload);
			} else {
				state.offerKeysWithExpandedCompany.splice(keyIndex, 1);
			}
		},
		setLastClickedRow: (state, { payload }) => {
			state.lastClickedRow = payload;
		},
		highlightRelatedOfferAndSource: (state) => {
			const backgroundColor = "#fff3f3";
			if (state.lastClickedRow.model_name === "SomOffer") {
				state.curItem.offers = state.curItem.offers?.map((sourceOffer) => {
					sourceOffer._style = null;
					return sourceOffer;
				});
				state.curItem.source_data = state.curItem.source_data?.map((sourceData) => {
					if (sourceData.source_data_id === state.lastClickedRow.source_data_id) {
						sourceData._style = { _row: { backgroundColor: backgroundColor } };
					} else {
						sourceData._style = {};
					}
					return sourceData;
				});
			} else if (state.lastClickedRow.model_name === "SomSourceData") {
				state.curItem.source_data = state.curItem.source_data?.map((sourceData) => {
					sourceData._style = null;
					return sourceData;
				});
				state.curItem.offers = state.curItem.offers?.map((sourceOffer) => {
					if (sourceOffer.source_data_id === state.lastClickedRow.source_data_id) {
						sourceOffer._style = { _row: { backgroundColor: backgroundColor } };
					} else {
						sourceOffer._style = {};
					}
					return sourceOffer;
				});
				if (state.pushSelectedSourceRowOfferToTop) {
					if (!state.lastClickedRow) return;

					state.curItem.offers = [
						...state.curItem.offers.filter((x) => x.source_data_id === state.lastClickedRow.source_data_id),
						...state.curItem.offers.filter((x) => x.source_data_id !== state.lastClickedRow.source_data_id),
					];
				}
			}
		},
		setPushSelectedSourceRowOfferToTop: (state, { payload }) => {
			state.pushSelectedSourceRowOfferToTop = payload;
		},
		setSourceInfoVisible: (state, { payload }) => {
			state.sourceInfoVisible = payload;
		},
		setSourceInfoPriceOfferId: (state, { payload }) => {
			state.sourceInfoPriceOfferId = payload;
		},
		setSourceInfoData: (state, { payload }) => {
			state.sourceInfoData = payload;
		},
		filterOffersBySourceDataRow: (state, { payload }) => {
			const { clickedSourceRow } = payload;

			let newCheckedVal = true;
			if (clickedSourceRow.dbChecked === true) newCheckedVal = false;

			state.curItem.source_data = state.curItem.source_data?.map((sourceRow) => {
				if (sourceRow.id === clickedSourceRow.id) {
					sourceRow.dbChecked = newCheckedVal;
				}
				return sourceRow;
			});
			const checkedSourceRowIds = state.curItem.source_data?.filter((row) => row.dbChecked === true)?.map((row) => row.source_data_id);
			state.curItem.offers = state.curItem.offers?.map((sourceOffer) => {
				if (checkedSourceRowIds.length === 0) {
					sourceOffer.dbVisible = true;
				} else {
					if (checkedSourceRowIds.includes(sourceOffer.source_data_id)) {
						sourceOffer.dbVisible = true;
					} else {
						sourceOffer.dbVisible = false;
					}
				}
				return sourceOffer;
			});
		},
		setInnSearchModalVisible: (state, { payload }) => {
			state.innSearchModalVisible = payload;
		},
		setInnSearchModalObj: (state, { payload }) => {
			state.innSearchModalObj = payload;
		},
		editCurItemCharUnit: (state, { payload }) => {
			state.changeAction.action = "EDIT";
			state.changeAction.key = "chars_unit";
			state.changeAction.payload = payload;
			if (increaseChangesCount) state.changesCount += 1;
		},
		pushOffersOfSelectedSourceToTop: (state) => {
			const selectedSourceRow = state.curItem.source_data?.find((x) => x.selected === true);
			if (!selectedSourceRow) return;

			state.curItem.offers = [
				...state.curItem.offers.filter((x) => x.source_data_id === selectedSourceRow.source_data_id),
				...state.curItem.offers.filter((x) => x.source_data_id !== selectedSourceRow.source_data_id),
			];
		},
		setSomLeftExpanded: (state, { payload }) => {
			state.somLeftExpanded = payload;
		},
		setSomLeftWidth: (state, { payload }) => {
			state.somLeftWidth = payload;
		},
		setSomShowMonitoring: (state, { payload }) => {
			state.somShowMonitoring = payload;
		},
		leafSomOfferMonitorPricesIndex: (state, { payload }) => {
			const direction = payload?.direction;
			if (direction === "next") {
				if (state.somOfferMonitoringIndex.MONITORING_3 < state.somOfferMonitoring?.length) {
					state.somOfferMonitoringIndex.MONITORING_1 += 1;
					state.somOfferMonitoringIndex.MONITORING_2 += 1;
					state.somOfferMonitoringIndex.MONITORING_3 += 1;
				}
			} else if (direction === "prev") {
				if (state.somOfferMonitoringIndex.MONITORING_1 > 0) {
					state.somOfferMonitoringIndex.MONITORING_1 -= 1;
					state.somOfferMonitoringIndex.MONITORING_2 -= 1;
					state.somOfferMonitoringIndex.MONITORING_3 -= 1;
				}
			}
		},
		setSomCurrentOfferMonitorPrice: (state, { payload }) => {
			state.somCurOfferMonitoring = payload;
		},
		setDeleteMonitoringConfirmDialog: (state, { payload }) => {
			state.deleteMonitoringConfirmDialog = payload;
		},
		setDeleteSourceDataConfirmDialog: (state, { payload }) => {
			state.deleteSourceDataConfirmDialog = payload;
		},
		setDeleteOfferConfirmDialog: (state, { payload }) => {
			state.deleteOfferConfirmDialog = payload;
		},
		setSomOfferPriceOfferDb: (state, { payload }) => {
			state.somOfferPriceOfferDb = { ...state.somOfferPriceOfferDb, ...payload };
		},
		selectItemOffer: (state, { payload }) => {
			const { offerId } = payload;
			state.curItem.offers = state.curItem.offers.map((sourceRow) => {
				if (sourceRow.id === offerId) {
					sourceRow.selected = true;
				} else {
					sourceRow.selected = false;
				}
				return sourceRow;
			});
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchSomItem.fulfilled, (state, { payload }) => {
			state.curItem = { ...payload, offers: updateSomOffer({ state, payload }) };
			state.lastClickedRow = {};
			state.fetchStatus = "success";
		});
		builder.addCase(editSomItem.fulfilled, (state) => {
			// state.curItem = { ...payload, offers: updateSomOffer({ state, payload }) };
			// state.lastClickedRow = {};
			state.changeAction = {};
			state.fetchStatus = "success";
		});
		builder.addCase(uploadSomOfferJusts.fulfilled, (state, { payload }) => {
			state.fetchStatus = "successAlert";
			state.fetchStatusText = payload;
		});
		builder.addCase(uploadSomOfferJusts.rejected, (state, { payload }) => {
			state.fetchStatus = "failed";
			if (payload?.message) state.fetchStatusText = payload.message;
		});

		builder.addCase(downloadSomOfferJust.rejected, (state, { payload }) => {
			state.fetchStatus = "failed";
			if (payload?.response?.status === 404) state.fetchStatusText = "Запрошенное обоснование не загружено на сервер";
		});
		builder.addCase(fetchSomOfferMonitoring.fulfilled, (state, action) => {
			updateSomOfferMonitoring({ state: state, payload: action.payload });
			state.curItem.offers = state.curItem.offers?.map((sourceOffer) => {
				if (sourceOffer.id === action?.meta?.arg?.somOfferId) {
					sourceOffer.selected = true;
				} else {
					sourceOffer.selected = false;
				}
				return sourceOffer;
			});
			state.fetchStatus = "success";
		});

		builder.addCase(createSomOfferMonitoring.fulfilled, (state, action) => {
			updateSomOfferMonitoring({ state: state, payload: action.payload });
			state.somCurOfferMonitoring = defaultSomCurOfferMonitoring;
			state.fetchStatus = "success";
		});

		builder.addCase(deleteSomOfferMonitoring.fulfilled, (state, action) => {
			updateSomOfferMonitoring({ state: state, payload: action.payload });
			state.deleteMonitoringConfirmDialog.visible = false;
			state.fetchStatus = "success";
		});

		builder.addMatcher(
			isAnyOf(
				fetchSomItem.pending,
				editSomItem.pending,
				uploadSomOfferJusts.pending,
				downloadSomOfferJust.pending,
				deleteSomOfferJust.pending,
				fetchSomOfferMonitoring.pending,
				createSomOfferMonitoring.pending,
				deleteSomOfferMonitoring.pending
			),
			(state, action) => {
				state.fetchStatus = "loading";
				state.fetchStatusText = undefined;
			}
		);

		builder.addMatcher(isAnyOf(downloadSomOfferJust.fulfilled, deleteSomOfferJust.fulfilled), (state, action) => {
			state.fetchStatus = "success";
		});

		builder.addMatcher(
			isAnyOf(
				fetchSomItem.rejected,
				editSomItem.rejected,
				deleteSomOfferJust.rejected,
				fetchSomOfferMonitoring.rejected,
				createSomOfferMonitoring.rejected,
				deleteSomOfferMonitoring.rejected
			),
			(state, action) => {
				state.fetchStatus = "failed";
			}
		);

		builder.addMatcher(isAnyOf(previewSomOfferJust.pending, previewSomOfferMonitoringJust.pending), (state, { payload }) => {
			state.fetchStatus = "loading";
			state.fetchStatusText = undefined;
			state.pdfBlobName = "file.pdf";
			state.pdfBlobData = null;
			state.justPdfVisible = false;
		});

		builder.addMatcher(isAnyOf(previewSomOfferJust.fulfilled, previewSomOfferMonitoringJust.fulfilled), (state, { payload }) => {
			state.pdfBlobName = payload.fileName;
			state.pdfBlobData = payload.data;
			state.justPdfVisible = true;
			state.fetchStatus = "success";
		});

		builder.addMatcher(isAnyOf(previewSomOfferJust.rejected, previewSomOfferMonitoringJust.rejected), (state, { payload }) => {
			state.fetchStatus = "failed";
			if (payload?.response?.status === 404) state.fetchStatusText = "Запрошенное обоснование не загружено на сервер";
		});
	},
});

export const {
	setTableData,
	setCurItem,
	editCurItemAttr,
	addCurItemChar,
	editCurItemChar,
	delCurItemChar,
	addPriceOfferCurItemOffer,
	editCurItemOffer,
	delCurItemOffer,
	addCurItemCls,
	editCurItemCls,
	delCurItemCls,
	increaseChangesCount,
	setChangeAction,
	editCurItemSourceData,
	setCurItemSourceData,
	setLastClickedRow,
	highlightRelatedOfferAndSource,
	setInnSearchModalVisible,
	setInnSearchModalObj,
	editCurItemCharUnit,
	setSomLeftExpanded,
	setSomLeftWidth,
	setSomShowMonitoring,
	leafSomOfferMonitorPricesIndex,
	setSomCurrentOfferMonitorPrice,
	setDeleteMonitoringConfirmDialog,
	setSomOfferPriceOfferDb,
} = somSlice.actions;

export const { actions: somActions } = somSlice;

export default somSlice.reducer;
